import { createElement, useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Eye, Loader2, Mail, RotateCcw, Save } from 'lucide-react'
import { toast } from 'sonner'
import type { NotificationTemplate } from '@/lib/api/features/notification-templates/types'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import useLoginStore from '@/store/LoginStore'
import { getTemplates } from '@/lib/api/features/notification-templates/api/getTemplates'
import { updateTemplate } from '@/lib/api/features/notification-templates/api/updateTemplate'
import { previewTemplate } from '@/lib/api/features/notification-templates/api/previewTemplate'
import { resetTemplate } from '@/lib/api/features/notification-templates/api/resetTemplate'
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_DISPLAY_NAMES,
} from '@/lib/api/features/notification-templates/types'

export default function TemplateManagementPage() {
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)

  const [templates, setTemplates] = useState<Array<NotificationTemplate>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editSubject, setEditSubject] = useState('')
  const [editBodyText, setEditBodyText] = useState('')
  const [editBodyHtml, setEditBodyHtml] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  )

  const templateVariables = useMemo(() => {
    if (!selectedTemplate?.templateVariables) return []
    try {
      return JSON.parse(selectedTemplate.templateVariables) as Array<string>
    } catch {
      return []
    }
  }, [selectedTemplate])

  const isDirty = useMemo(() => {
    if (!selectedTemplate) return false
    return (
      editSubject !== selectedTemplate.subject ||
      editBodyText !== selectedTemplate.bodyText ||
      editBodyHtml !== (selectedTemplate.bodyHtml ?? '')
    )
  }, [selectedTemplate, editSubject, editBodyText, editBodyHtml])

  const loadTemplates = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await getTemplates(workspaceSlug)
      setTemplates(result.templates)
      const first = result.templates[0]
      if (first && !selectedId) {
        setSelectedId(first.id)
        setEditSubject(first.subject)
        setEditBodyText(first.bodyText)
        setEditBodyHtml(first.bodyHtml ?? '')
      }
    } catch {
      toast.error('Failed to load templates')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceSlug, selectedId])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const selectTemplate = useCallback((template: NotificationTemplate) => {
    setSelectedId(template.id)
    setEditSubject(template.subject)
    setEditBodyText(template.bodyText)
    setEditBodyHtml(template.bodyHtml ?? '')
    setPreviewHtml(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedTemplate) return
    setIsSaving(true)
    try {
      const updated = await updateTemplate(workspaceSlug, selectedTemplate.id, {
        subject: editSubject,
        bodyText: editBodyText,
        bodyHtml: editBodyHtml || undefined,
      })
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      toast.success('Template saved')
    } catch {
      toast.error('Failed to save template')
    } finally {
      setIsSaving(false)
    }
  }, [workspaceSlug, selectedTemplate, editSubject, editBodyText, editBodyHtml])

  const handleReset = useCallback(async () => {
    if (!selectedTemplate) return
    if (!confirm(`Reset "${TEMPLATE_DISPLAY_NAMES[selectedTemplate.templateType] ?? selectedTemplate.templateType}" to its default? This cannot be undone.`))
      return
    setIsResetting(true)
    try {
      const updated = await resetTemplate(workspaceSlug, selectedTemplate.templateType)
      setTemplates((prev) => prev.map((t) => (t.templateType === updated.templateType ? updated : t)))
      setEditSubject(updated.subject)
      setEditBodyText(updated.bodyText)
      setEditBodyHtml(updated.bodyHtml ?? '')
      toast.success('Template reset to default')
    } catch {
      toast.error('Failed to reset template')
    } finally {
      setIsResetting(false)
    }
  }, [workspaceSlug, selectedTemplate])

  const handlePreview = useCallback(async () => {
    setIsPreviewing(true)
    try {
      const result = await previewTemplate(workspaceSlug, {
        subject: editSubject,
        bodyText: editBodyText,
        bodyHtml: editBodyHtml,
        templateVariables: selectedTemplate?.templateVariables ?? undefined,
      })
      setPreviewHtml(result.bodyHtml || result.bodyText)
    } catch {
      toast.error('Failed to render preview')
    } finally {
      setIsPreviewing(false)
    }
  }, [workspaceSlug, editSubject, editBodyText, editBodyHtml, selectedTemplate])

  // Group templates by category
  const groupedTemplates = useMemo(() => {
    const groups: Array<{ label: string; templates: Array<NotificationTemplate> }> = []
    for (const [, category] of Object.entries(TEMPLATE_CATEGORIES)) {
      const items = templates.filter((t) => category.types.includes(t.templateType))
      if (items.length > 0) {
        groups.push({ label: category.label, templates: items })
      }
    }
    // Add any uncategorized templates
    const categorized = new Set(Object.values(TEMPLATE_CATEGORIES).flatMap((c) => c.types))
    const uncategorized = templates.filter((t) => !categorized.has(t.templateType))
    if (uncategorized.length > 0) {
      groups.push({ label: 'Other', templates: uncategorized })
    }
    return groups
  }, [templates])

  return (
    <SettingsPageContainer>
        <SettingsPageHeader
          title="Email Templates"
          titleAdornment={<Badge variant="outline" className="text-xs">Dev</Badge>}
        />

        {isLoading ? (
          <div className="flex h-64 items-center justify-center rounded-md border bg-card">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-[240px_1fr] gap-6">
            {/* Template List (Left Panel) */}
            <div className="space-y-4">
              {groupedTemplates.map((group) => (
                <div key={group.label}>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => selectTemplate(template)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedId === template.id
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="size-3.5 shrink-0" />
                          <span className="truncate">
                            {TEMPLATE_DISPLAY_NAMES[template.templateType] ?? template.templateType}
                          </span>
                        </div>
                        {!template.isActive && (
                          <Badge variant="outline" className="mt-1 text-[10px]">Inactive</Badge>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Editor (Right Panel) */}
            {selectedTemplate ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">
                      {TEMPLATE_DISPLAY_NAMES[selectedTemplate.templateType] ?? selectedTemplate.templateType}
                    </h2>
                    {selectedTemplate.description && (
                      <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleReset} disabled={isResetting}>
                      {isResetting ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
                      <span className="ml-1.5">Reset</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={handlePreview} disabled={isPreviewing}>
                      {isPreviewing ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-4" />}
                      <span className="ml-1.5">Preview</span>
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
                      {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                      <span className="ml-1.5">Save</span>
                    </Button>
                  </div>
                </div>

                {/* Available Variables */}
                {templateVariables.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs text-muted-foreground mr-1">Variables:</span>
                    {templateVariables.map((v) => (
                      <Badge key={v} variant="secondary" className="text-[11px] font-mono">
                        {'{{ ' + v + ' }}'}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
                  <Input
                    id="subject"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                {/* Body tabs */}
                <Tabs defaultValue="text">
                  <TabsList>
                    <TabsTrigger value="text">Plain Text</TabsTrigger>
                    <TabsTrigger value="html">HTML</TabsTrigger>
                    {previewHtml && <TabsTrigger value="preview">Preview</TabsTrigger>}
                  </TabsList>
                  <TabsContent value="text" className="mt-3">
                    <Textarea
                      value={editBodyText}
                      onChange={(e) => setEditBodyText(e.target.value)}
                      className="font-mono text-sm min-h-[400px] resize-y"
                    />
                  </TabsContent>
                  <TabsContent value="html" className="mt-3">
                    <Textarea
                      value={editBodyHtml}
                      onChange={(e) => setEditBodyHtml(e.target.value)}
                      className="font-mono text-sm min-h-[400px] resize-y"
                    />
                  </TabsContent>
                  {previewHtml && (
                    <TabsContent value="preview" className="mt-3">
                      <div className="border rounded-md bg-card p-4 min-h-[400px]">
                        <iframe
                          srcDoc={previewHtml}
                          title="Template preview"
                          className="w-full min-h-[380px] border-0"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 rounded-md border bg-card text-center">
                <Mail className="size-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a template to edit</p>
              </div>
            )}
          </div>
        )}
    </SettingsPageContainer>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/templates',
)({
  beforeLoad: ({ params }) => {
    if (import.meta.env.PROD) {
      throw redirect({
        to: '/$workspaceSlug/settings',
        params: { workspaceSlug: params.workspaceSlug },
      })
    }
  },
  component: TemplateManagementPage,
})
